import type { Broker } from '@/shared/api/resources';
import { formatDocument, formatRate } from '@/shared/lib/format';
import { Icon } from '@/shared/ui/Icon';
import { Kanban, type KanbanColumn } from '@/shared/ui/Kanban';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusDescriptor, statusLabel } from '@/shared/ui/statusLabels';

/**
 * Esteira de ativacao do corretor, em colunas.
 *
 * A tabela responde "qual e a situacao deste corretor". O quadro responde outra
 * pergunta, que e a que a operacao faz todo dia: quantos estao parados em cada
 * etapa, e o que falta para destravar. Sao dois usos diferentes do mesmo cadastro,
 * e por isso as duas visoes existem.
 *
 * O quadro em si e o comum (`shared/ui/Kanban`, P-FUN-2b). Aqui ficam o que e do
 * corretor: as etapas, o cartao e os destinos, que vem do servidor em `next_stages`.
 */

/** Ordem das colunas, que e a ordem da esteira. */
const etapas = [
  'novo',
  'em_documentacao',
  'em_validacao',
  'ativo',
  'bloqueado',
] as const;

const colunas: KanbanColumn[] = etapas.map((etapa) => ({
  key: etapa,
  label: statusLabel('corretor', etapa),
  tone: statusDescriptor('corretor', etapa)?.tone,
}));

export interface BrokerKanbanProps {
  corretores: Broker[];
  carregando: boolean;
  podeMover: boolean;
  onMover: (corretor: Broker, etapa: string) => void;
  onAbrir: (corretor: Broker) => void;
}

export function BrokerKanban({
  corretores,
  carregando,
  podeMover,
  onMover,
  onAbrir,
}: BrokerKanbanProps) {
  return (
    <Kanban
      columns={colunas}
      items={corretores}
      keyOf={(corretor) => corretor.id}
      columnOf={(corretor) => corretor.stage}
      targetsOf={(corretor) => corretor.next_stages}
      canMove={podeMover}
      onMove={onMover}
      onOpen={onAbrir}
      loading={carregando}
      loadingLabel="Carregando corretores"
      emptyLabel="Nenhum corretor"
      renderCard={(corretor) => (
        <>
          <p className="line-clamp-2 text-xs leading-snug font-semibold wrap-anywhere text-ink-900">
            {corretor.legal_name}
          </p>
          <p className="mt-0.5 truncate text-2xs text-ink-500 tabular-nums">
            {formatDocument(corretor.document)}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <StatusBadge kind="situacaoExterna" value={corretor.external_status} />
            <span className="text-2xs text-ink-500 tabular-nums">
              {formatRate(corretor.commission_bps)}
            </span>
          </div>

          {corretor.commercial_owner ? (
            <p className="mt-2 flex items-center gap-1 border-t border-line pt-1.5 text-2xs text-ink-500">
              <Icon name="users" size={11} className="shrink-0" />
              <span className="truncate">{corretor.commercial_owner}</span>
            </p>
          ) : null}
        </>
      )}
    />
  );
}
